import React, { useState, useRef } from 'react';
import { Button, Form, Space, InputNumber, message } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { t } from '../../../../i18n';
import { formatLengthCmToInch } from '../../../../utils/numberUtils';

export interface ActualDimensionInfo {
    lengthCm: number | null;
    widthCm: number | null;
    heightCm: number | null;
}

interface Props {
    value?: ActualDimensionInfo;
    onChange?: (val: ActualDimensionInfo) => void;
    editingBlock?: null | 'recipient' | 'dimension';
    onEdit?: () => void;
    onEditCancel?: () => void;
    onSave?: (formData: ActualDimensionInfo) => Promise<void>;
    disabled?: boolean;
}

/**
 * 将英寸转换为厘米
 */
const convertInchToCm = (inch: number | null | undefined): number | null => {
    if (inch === null || inch === undefined) return null;
    return inch * 2.54;
};

/**
 * 将厘米转换为英寸
 */
const convertCmToInch = (cm: number | null | undefined): number | null => {
    if (cm === null || cm === undefined) return null;
    return cm / 2.54;
};

const ActualDimensionEditor: React.FC<Props> = ({
    value,
    onChange,
    editingBlock,
    onEdit,
    onEditCancel,
    onSave,
    disabled = false
}) => {
    const [editing, setEditing] = useState(false);
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const lengthInputRef = useRef<any>(null);
    const widthInputRef = useRef<any>(null);
    const heightInputRef = useRef<any>(null);

    // 创建处理粘贴事件的函数
    const createPasteHandler = (fieldName: string) => (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        // 只保留数字和小数点
        const digits = pastedText.replace(/[^\d.]/g, '');
        // 只保留第一个小数点
        const parts = digits.split('.');
        const cleaned = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : digits;
        if (cleaned) {
            const num = parseFloat(cleaned);
            if (!isNaN(num) && num >= 0) {
                form.setFieldValue(fieldName, num);
            }
        }
    };

    // 创建处理输入变化的函数，确保只有有效数字才能被设置
    const createChangeHandler = (fieldName: string) => (value: number | null) => {
        // 如果值是 null 或 undefined，允许（表示清空）
        if (value === null || value === undefined) {
            form.setFieldValue(fieldName, null);
            return;
        }
        // 验证值是否为有效数字且 >= 0
        if (typeof value === 'number' && !isNaN(value) && value >= 0 && isFinite(value)) {
            form.setFieldValue(fieldName, value);
        } else {
            // 如果值无效，恢复为 null 或当前有效值
            const currentValue = form.getFieldValue(fieldName);
            if (currentValue === null || currentValue === undefined || isNaN(currentValue) || currentValue < 0) {
                form.setFieldValue(fieldName, null);
            }
        }
    };

    const handleEdit = () => {
        if (disabled) return;
        // 将厘米转换为英寸用于显示和编辑
        form.setFieldsValue({
            lengthInch: value?.lengthCm ? convertCmToInch(value.lengthCm) : null,
            widthInch: value?.widthCm ? convertCmToInch(value.widthCm) : null,
            heightInch: value?.heightCm ? convertCmToInch(value.heightCm) : null,
        });
        setEditing(true);
        if (onEdit) onEdit();
    };

    const handleCancel = () => {
        setEditing(false);
        form.resetFields();
        if (onEditCancel) onEditCancel();
    };

    const handleSave = async () => {
        try {
            // 强制触发表单验证
            await form.validateFields();
            
            // 获取验证后的值
            const values = form.getFieldsValue();
            const rawValues = [values.lengthInch, values.widthInch, values.heightInch];
            
            // 验证每个值（任何一个都不能为空或0）
            const validationResults = rawValues.map((v, index) => {
                const fieldNames = [t('delivery.parcels.label.length'), t('delivery.parcels.label.width'), t('delivery.parcels.label.height')];
                const fieldName = fieldNames[index];
                
                // 检查是否为空
                if (v === null || v === undefined || v === '' || (typeof v === 'string' && v.trim() === '')) {
                    return { valid: false, reason: `${fieldName}不能为空` };
                }
                
                // 检查是否为字符串类型
                if (typeof v === 'string') {
                    // 检查是否包含非数字字符（除了小数点）
                    if (/[^\d.]/.test(v)) {
                        return { valid: false, reason: `${fieldName}包含非数字字符` };
                    }
                    // 尝试转换为数字
                    const num = parseFloat(v);
                    if (isNaN(num) || !isFinite(num)) {
                        return { valid: false, reason: `${fieldName}无法转换为有效数字` };
                    }
                    if (num < 0) {
                        return { valid: false, reason: `${fieldName}不能为负数` };
                    }
                    if (num === 0) {
                        return { valid: false, reason: `${fieldName}不能为0` };
                    }
                    return { valid: true, value: num };
                }
                
                // 检查是否为数字类型
                if (typeof v === 'number') {
                    if (isNaN(v) || !isFinite(v)) {
                        return { valid: false, reason: `${fieldName}不是有效数字` };
                    }
                    if (v < 0) {
                        return { valid: false, reason: `${fieldName}不能为负数` };
                    }
                    if (v === 0) {
                        return { valid: false, reason: `${fieldName}不能为0` };
                    }
                    return { valid: true, value: v };
                }
                
                // 其他类型都视为无效
                return { valid: false, reason: `${fieldName}类型无效` };
            });
            
            // 检查是否有无效值
            const invalidResult = validationResults.find(r => !r.valid);
            if (invalidResult) {
                if (invalidResult.reason?.includes('不能为空') || invalidResult.reason?.includes('不能为0')) {
                    message.warning(t('delivery.parcels.message.dimensionCannotBeEmptyOrZero') || '尺寸不能为空或0');
                } else {
                    message.warning(t('delivery.parcels.message.dimensionMustBeNumber') || '尺寸必须为有效数字');
                }
                return;
            }
            
            // 第三步：提取有效值
            const safeLengthInch = validationResults[0].value;
            const safeWidthInch = validationResults[1].value;
            const safeHeightInch = validationResults[2].value;
            
            // 将英寸转换回厘米
            const dimensionInfo: ActualDimensionInfo = {
                lengthCm: convertInchToCm(safeLengthInch),
                widthCm: convertInchToCm(safeWidthInch),
                heightCm: convertInchToCm(safeHeightInch),
            };

            if (onSave) {
                setSaving(true);
                await onSave(dimensionInfo);
                setSaving(false);
            }

            if (onChange) {
                onChange(dimensionInfo);
            }

            setEditing(false);
            if (onEditCancel) onEditCancel();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    // 显示模式
    const displayValue = value && (
        [value.lengthCm, value.widthCm, value.heightCm].every(v => !v)
            ? '-'
            : [
                formatLengthCmToInch(value.lengthCm),
                formatLengthCmToInch(value.widthCm),
                formatLengthCmToInch(value.heightCm)
            ]
                .filter(Boolean)
                .join(' * ')
    );

    return (
        <div className="mb-3 relative">
            <div style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}>
                {editing ? (null) : (
                    (!editingBlock && !disabled) && (
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={handleEdit}
                            type="text"
                        />
                    )
                )}
            </div>
            <div className="text-gray-500 text-sm mb-1">
                {t('delivery.parcels.label.actDimension')}:
            </div>
            {editing ? (
                <Form form={form} layout="vertical" className="relative">
                    <Form.Item
                        label={t('delivery.parcels.label.dimension')}
                        required={false}
                    >
                        <div className="flex items-center gap-2">
                            <Form.Item
                                name="lengthInch"
                                noStyle
                                rules={[
                                    {
                                        type: 'number',
                                        min: 0,
                                        message: t('delivery.parcels.message.dimensionMustBePositive')
                                    }
                                ]}
                            >
                                <InputNumber
                                    ref={lengthInputRef}
                                    placeholder={t('delivery.parcels.label.length')}
                                    style={{ width: '30%' }}
                                    min={0}
                                    precision={2}
                                    addonAfter="in"
                                    onChange={createChangeHandler('lengthInch')}
                                    onPaste={createPasteHandler('lengthInch')}
                                    onBlur={() => {
                                        // 失焦时清理无效值
                                        const value = form.getFieldValue('lengthInch');
                                        if (value !== null && value !== undefined) {
                                            if (typeof value === 'string') {
                                                const num = parseFloat(value.replace(/[^\d.]/g, ''));
                                                if (!isNaN(num) && isFinite(num) && num >= 0) {
                                                    form.setFieldValue('lengthInch', num);
                                                } else {
                                                    form.setFieldValue('lengthInch', null);
                                                }
                                            }
                                        }
                                    }}
                                    formatter={(value) => {
                                        if (value === undefined || value === null) return '';
                                        // 格式化为2位小数
                                        const num = typeof value === 'number' ? value : parseFloat(String(value));
                                        if (isNaN(num)) return '';
                                        return num.toFixed(2);
                                    }}
                                />
                            </Form.Item>
                            <span>*</span>
                            <Form.Item
                                name="widthInch"
                                noStyle
                                rules={[
                                    {
                                        type: 'number',
                                        min: 0,
                                        message: t('delivery.parcels.message.dimensionMustBePositive')
                                    }
                                ]}
                            >
                                <InputNumber
                                    ref={widthInputRef}
                                    placeholder={t('delivery.parcels.label.width')}
                                    style={{ width: '30%' }}
                                    min={0}
                                    precision={2}
                                    addonAfter="in"
                                    onChange={createChangeHandler('widthInch')}
                                    onPaste={createPasteHandler('widthInch')}
                                    onBlur={() => {
                                        // 失焦时清理无效值
                                        const value = form.getFieldValue('widthInch');
                                        if (value !== null && value !== undefined) {
                                            if (typeof value === 'string') {
                                                const num = parseFloat(value.replace(/[^\d.]/g, ''));
                                                if (!isNaN(num) && isFinite(num) && num >= 0) {
                                                    form.setFieldValue('widthInch', num);
                                                } else {
                                                    form.setFieldValue('widthInch', null);
                                                }
                                            }
                                        }
                                    }}
                                    formatter={(value) => {
                                        if (value === undefined || value === null) return '';
                                        // 格式化为2位小数
                                        const num = typeof value === 'number' ? value : parseFloat(String(value));
                                        if (isNaN(num)) return '';
                                        return num.toFixed(2);
                                    }}
                                />
                            </Form.Item>
                            <span>*</span>
                            <Form.Item
                                name="heightInch"
                                noStyle
                                rules={[
                                    {
                                        type: 'number',
                                        min: 0,
                                        message: t('delivery.parcels.message.dimensionMustBePositive')
                                    }
                                ]}
                            >
                                <InputNumber
                                    ref={heightInputRef}
                                    placeholder={t('delivery.parcels.label.height')}
                                    style={{ width: '30%' }}
                                    min={0}
                                    precision={2}
                                    addonAfter="in"
                                    onChange={createChangeHandler('heightInch')}
                                    onPaste={createPasteHandler('heightInch')}
                                    onBlur={() => {
                                        // 失焦时清理无效值
                                        const value = form.getFieldValue('heightInch');
                                        if (value !== null && value !== undefined) {
                                            if (typeof value === 'string') {
                                                const num = parseFloat(value.replace(/[^\d.]/g, ''));
                                                if (!isNaN(num) && isFinite(num) && num >= 0) {
                                                    form.setFieldValue('heightInch', num);
                                                } else {
                                                    form.setFieldValue('heightInch', null);
                                                }
                                            }
                                        }
                                    }}
                                    formatter={(value) => {
                                        if (value === undefined || value === null) return '';
                                        // 格式化为2位小数
                                        const num = typeof value === 'number' ? value : parseFloat(String(value));
                                        if (isNaN(num)) return '';
                                        return num.toFixed(2);
                                    }}
                                />
                            </Form.Item>
                        </div>
                    </Form.Item>
                    <Form.Item label="" style={{ marginBottom: '10px' }}>
                        <div className="absolute bottom-0 right-0 z-10">
                            <Space>
                                <Button
                                    icon={<CheckOutlined />}
                                    size="small"
                                    onClick={handleSave}
                                    loading={saving}
                                />
                                <Button
                                    icon={<CloseOutlined />}
                                    size="small"
                                    onClick={handleCancel}
                                    disabled={saving}
                                />
                            </Space>
                        </div>
                    </Form.Item>
                </Form>
            ) : (
                <div className="break-words">{displayValue || '-'}</div>
            )}
        </div>
    );
};

export default ActualDimensionEditor;

